/**
 * TeamAvailabilityCalendarPage — admin-only org-wide heatmap.
 *
 * Y-axis: every active named_resource, grouped by role.
 * X-axis: the next N weeks (default 26).
 * Cell colour: load ratio (allocated hours / weekly capacity).
 *   • blocked PTO → diagonal-striped grey
 *   • idle (≤25%) → slate
 *   • healthy (≤80%) → emerald
 *   • loaded (≤100%) → amber
 *   • overbooked (>100%) → rose
 *
 * Hovering a cell reveals the exact allocated hours, capacity, and ratio.
 * Top toolbar exposes a weeks slider (12 / 26 / 52) and a CSV export.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, CalendarRange, AlertTriangle, Download, RefreshCw,
  Users as UsersIcon, CalendarOff, Flame, Mail, Sparkles,
} from 'lucide-react';
import SuggestReassignmentDialog from '../components/SuggestReassignmentDialog';
import { EntityRegionFilterChips, useEntityRegionOptions } from '../components/EntityRegionFilterChips';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import PortfolioHeatmapView from './PortfolioHeatmapView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cell colour for an absolute load ratio (0 → idle, 1 → at capacity).
// We keep the palette tight to mirror RosterView's load story.
const cellStyleFor = ({ blocked, hours, capacity }) => {
  if (blocked) {
    return {
      bg: 'bg-slate-200',
      border: 'border-slate-300',
      text: 'text-slate-400',
      pattern: 'bg-[repeating-linear-gradient(45deg,#cbd5e1_0,#cbd5e1_2px,transparent_2px,transparent_6px)]',
      label: 'PTO',
    };
  }
  const ratio = capacity > 0 ? hours / capacity : 0;
  if (ratio <= 0.05) return { bg: 'bg-slate-50',   border: 'border-slate-200', text: 'text-slate-400', label: 'Idle' };
  if (ratio <= 0.25) return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'Light' };
  if (ratio <= 0.80) return { bg: 'bg-emerald-200', border: 'border-emerald-300', text: 'text-emerald-800', label: 'Healthy' };
  if (ratio <= 1.00) return { bg: 'bg-amber-200',  border: 'border-amber-300', text: 'text-amber-900', label: 'Loaded' };
  if (ratio <= 1.5)  return { bg: 'bg-rose-300',   border: 'border-rose-400', text: 'text-rose-900', label: 'Overbooked' };
  return                  { bg: 'bg-rose-500',   border: 'border-rose-600', text: 'text-white', label: 'Severe' };
};

// Format an ISO Monday date into "May 25" for header labels.
const fmtWeekLabel = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch { return iso; }
};

const ROLE_ORDER = ['PM', 'CS', 'CF', 'IN', 'DM', 'DE', 'IF', 'HC', 'UX', 'TE'];

export default function TeamAvailabilityCalendarPage() {
  const [tab, setTab] = useState('availability');
  return (
    <div className="w-full px-4 py-6" data-testid="resource-planning-page">
      <div className="mb-4">
        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider mb-1">
          <CalendarRange className="w-3.5 h-3.5" /> Leadership View
        </div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="resource-planning-title">
          Resource Planning
        </h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="availability" data-testid="resource-planning-tab-availability">
            <CalendarRange className="w-3.5 h-3.5 mr-1.5" /> Team Availability
          </TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="resource-planning-tab-heatmap">
            <Flame className="w-3.5 h-3.5 mr-1.5" /> Portfolio Heat-map
          </TabsTrigger>
        </TabsList>
        <TabsContent value="availability">
          <AvailabilityCalendarView />
        </TabsContent>
        <TabsContent value="heatmap">
          <PortfolioHeatmapView />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AvailabilityCalendarView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weeks, setWeeks] = useState(26);
  const [hoverCell, setHoverCell] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [reassignTarget, setReassignTarget] = useState(null); // { resourceId, weekIso } | null
  // Optional custom-range state. When either is set the request uses
  // start_date/end_date in place of `weeks`. Pickers always show ISO
  // dates (YYYY-MM-DD) so they're keyboard-friendly.
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rangeMode, setRangeMode] = useState('preset'); // 'preset' | 'custom'
  // Entity / Region filters — match resources by their assigned entity/regions,
  // remembered per browser between visits.
  const [entityFilter, setEntityFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp-teamavail-erf') || '{}').e || []; } catch { return []; }
  });
  const [regionFilter, setRegionFilter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bp-teamavail-erf') || '{}').r || []; } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem('bp-teamavail-erf', JSON.stringify({ e: entityFilter, r: regionFilter }));
  }, [entityFilter, regionFilter]);
  const { entities: erEntities, regions: erRegions } = useEntityRegionOptions();

  const load = (opts = {}) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (opts.mode === 'custom' || (rangeMode === 'custom' && !opts.mode)) {
      const s = opts.start ?? customStart;
      const e = opts.end ?? customEnd;
      if (s) params.set('start_date', s);
      if (e) params.set('end_date', e);
      if (!s && !e) params.set('weeks', String(opts.weeks ?? weeks));
    } else {
      params.set('weeks', String(opts.weeks ?? weeks));
    }
    if (entityFilter.length) params.set('entity', entityFilter.join(','));
    if (regionFilter.length) params.set('region', regionFilter.join(','));
    axios.get(`${API_URL}/api/team-availability?${params.toString()}`, { withCredentials: true })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((err) => {
        const detail = err?.response?.data?.detail || err?.message || 'Failed to load';
        setError(detail);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (rangeMode === 'preset') load({ weeks });
    else if (customStart && customEnd) load({ mode: 'custom' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, rangeMode, entityFilter, regionFilter]);

  const applyCustomRange = () => {
    if (!customStart || !customEnd) return;
    load({ mode: 'custom', start: customStart, end: customEnd });
  };

  // Group resources by role, keeping the canonical role order.
  const grouped = useMemo(() => {
    if (!data?.resources) return [];
    const byRole = {};
    for (const r of data.resources) {
      const filtered = roleFilter === 'all' || roleFilter === r.role;
      if (!filtered) continue;
      (byRole[r.role] = byRole[r.role] || []).push(r);
    }
    const ordered = [];
    for (const code of ROLE_ORDER) {
      if (byRole[code]) ordered.push({ role: code, resources: byRole[code] });
    }
    // Any role not in canonical list (defensive)
    for (const code of Object.keys(byRole)) {
      if (!ROLE_ORDER.includes(code)) ordered.push({ role: code, resources: byRole[code] });
    }
    return ordered;
  }, [data, roleFilter]);

  const roleLabels = data?.role_labels || {};
  const summary = data?.summary || {};

  const onExportCsv = () => {
    if (!data?.resources) return;
    const lines = [];
    const header = ['name', 'email', 'role', 'weekly_capacity_hours', ...data.week_labels];
    lines.push(header.join(','));
    for (const r of data.resources) {
      const row = [
        r.name?.replace(/,/g, ' ') ?? '',
        r.email ?? '',
        r.role ?? '',
        r.weekly_capacity_hours ?? '',
        ...r.weekly_hours.map((h, i) => r.blocked_week_indices.includes(i) ? 'PTO' : h),
      ];
      lines.push(row.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-availability-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="team-availability-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-4" data-testid="team-availability-error">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-800">Couldn't load team availability</div>
                <div className="text-sm text-slate-600 mt-1">{error}</div>
                <Button
                  className="mt-3"
                  variant="outline"
                  size="sm"
                  onClick={() => load({ weeks })}
                  data-testid="team-availability-retry-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allRoleCodes = Array.from(new Set((data?.resources || []).map(r => r.role)));

  return (
    <div className="w-full" data-testid="team-availability-view">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-600 max-w-2xl" data-testid="team-availability-title">
            {rangeMode === 'custom' && data?.start_date && data?.end_date ? (
              <>Org-wide capacity from <span className="font-semibold">{data.start_date}</span> to <span className="font-semibold">{data.end_date}</span> ({data?.weeks_n ?? weeks} weeks). Each cell shows allocated hours vs that person's weekly capacity. Diagonal hatching = blocked PTO.</>
            ) : (
              <>Org-wide capacity for the next {data?.weeks_n ?? weeks} weeks. Each cell shows allocated hours vs that person's weekly capacity. Diagonal hatching = blocked PTO.</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[12, 26, 52].map((n) => (
            <Button
              key={n}
              variant={rangeMode === 'preset' && weeks === n ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setRangeMode('preset'); setWeeks(n); }}
              data-testid={`team-availability-weeks-${n}-btn`}
            >
              {n}w
            </Button>
          ))}
          <div className="flex items-center gap-1 border-l pl-2 ml-1">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="h-8 text-xs border rounded px-2 bg-white"
              aria-label="Start date"
              data-testid="team-availability-start-date"
            />
            <span className="text-xs text-slate-400">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-8 text-xs border rounded px-2 bg-white"
              aria-label="End date"
              data-testid="team-availability-end-date"
            />
            <Button
              variant={rangeMode === 'custom' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setRangeMode('custom'); applyCustomRange(); }}
              disabled={!customStart || !customEnd}
              data-testid="team-availability-apply-range-btn"
            >
              Apply
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ weeks })}
            data-testid="team-availability-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onExportCsv}
            disabled={!data?.resources?.length}
            data-testid="team-availability-export-csv-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Entity / Region filters — match each resource's assignment */}
      {(erEntities.length > 0 || erRegions.length > 0) && (
        <div className="flex flex-wrap items-start gap-5 mb-5" data-testid="teamavail-entity-region-filters">
          <EntityRegionFilterChips
            entities={erEntities} regions={erRegions}
            entityFilter={entityFilter} regionFilter={regionFilter}
            setEntityFilter={setEntityFilter} setRegionFilter={setRegionFilter}
            idPrefix="teamavail" />
          {(entityFilter.length > 0 || regionFilter.length > 0) && (
            <button onClick={() => { setEntityFilter([]); setRegionFilter([]); }}
              className="text-[11px] text-slate-600 underline mt-4 hover:text-[#0F2040] px-1 py-0.5" data-testid="teamavail-filters-clear">clear</button>
          )}
        </div>
      )}

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <SummaryChip
          icon={<UsersIcon className="w-4 h-4 text-slate-500" />}
          label="Resources"
          value={summary.total_resources ?? 0}
          testid="team-availability-summary-resources"
        />
        <SummaryChip
          icon={<CalendarRange className="w-4 h-4 text-slate-500" />}
          label="Estimates in scope"
          value={summary.total_estimates_in_scope ?? 0}
          testid="team-availability-summary-estimates"
        />
        <SummaryChip
          icon={<Flame className="w-4 h-4 text-rose-500" />}
          label="Overbooked cells"
          value={summary.total_overbooked_cells ?? 0}
          tone={summary.total_overbooked_cells > 0 ? 'rose' : 'slate'}
          testid="team-availability-summary-overbooked"
        />
        <SummaryChip
          icon={<CalendarOff className="w-4 h-4 text-slate-500" />}
          label="PTO cells"
          value={summary.total_blocked_cells ?? 0}
          testid="team-availability-summary-pto"
        />
      </div>

      {/* Region capacity rollup — where to hire next */}
      {(data?.region_rollup || []).length > 0 && (
        <div className="mb-6" data-testid="region-capacity-rollup">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
            Region capacity · {data?.weeks_n}w window
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.region_rollup.map(b => {
              const pct = b.utilization_pct;
              const tone = pct == null ? 'bg-slate-300'
                : pct > 100 ? 'bg-rose-500' : pct > 80 ? 'bg-amber-400' : 'bg-emerald-500';
              const txt = pct == null ? 'text-slate-500'
                : pct > 100 ? 'text-rose-600' : pct > 80 ? 'text-amber-600' : 'text-emerald-700';
              return (
                <Card key={b.region} data-testid={`region-rollup-${b.region.toLowerCase()}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#0F2040]">{b.region}</span>
                      <span className="text-[10px] text-slate-400">{b.resources} {b.resources === 1 ? 'person' : 'people'}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 tabular-nums">
                      {b.committed_hours.toLocaleString()}h committed / {b.capacity_hours.toLocaleString()}h capacity
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden">
                      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(pct ?? 0, 100)}%` }} />
                    </div>
                    <div className={`text-[11px] font-bold mt-1 ${txt}`} data-testid={`region-rollup-pct-${b.region.toLowerCase()}`}>
                      {pct == null ? '—' : `${pct}% utilised`}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Role filter */}
      {allRoleCodes.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">Role:</span>
          <Button
            variant={roleFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRoleFilter('all')}
            data-testid="team-availability-role-filter-all"
          >
            All
          </Button>
          {ROLE_ORDER.filter(c => allRoleCodes.includes(c)).map((code) => (
            <Button
              key={code}
              variant={roleFilter === code ? 'default' : 'outline'}
              size="sm"
              onClick={() => setRoleFilter(code)}
              data-testid={`team-availability-role-filter-${code}`}
            >
              {roleLabels[code] || code}
            </Button>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-slate-600">
        <LegendChip color="bg-slate-50 border-slate-200" label="Idle" />
        <LegendChip color="bg-emerald-50 border-emerald-200" label="Light" />
        <LegendChip color="bg-emerald-200 border-emerald-300" label="Healthy" />
        <LegendChip color="bg-amber-200 border-amber-300" label="Loaded" />
        <LegendChip color="bg-rose-300 border-rose-400" label="Overbooked" />
        <LegendChip color="bg-rose-500 border-rose-600" label="Severe" />
        <LegendChip
          color="bg-slate-200 border-slate-300 bg-[repeating-linear-gradient(45deg,#cbd5e1_0,#cbd5e1_2px,transparent_2px,transparent_6px)]"
          label="PTO"
        />
      </div>

      {/* Empty state */}
      {grouped.length === 0 ? (
        <Card data-testid="team-availability-empty">
          <CardContent className="py-12 text-center text-slate-500">
            {(entityFilter.length > 0 || regionFilter.length > 0) ? (
              <>
                No resources match the selected entity/region filters.{' '}
                <button onClick={() => { setEntityFilter([]); setRegionFilter([]); }}
                  className="text-[#4ABFCF] underline hover:text-[#0F2040]"
                  data-testid="team-availability-empty-clear-filters">Clear filters</button>
              </>
            ) : (
              <>No active named resources yet. Add some in <span className="font-semibold">Admin → Named Resources</span>.</>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="border-b">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <UsersIcon className="w-4 h-4 text-slate-500" />
              {summary.total_resources} {summary.total_resources === 1 ? 'resource' : 'resources'} · {data?.weeks_n} weeks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="team-availability-grid">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 bg-slate-50 z-20 text-left px-3 py-2 border-b border-r font-semibold text-slate-700 min-w-[220px]">
                    Resource
                  </th>
                  <th className="text-right px-2 py-2 border-b border-r font-semibold text-slate-700 whitespace-nowrap">
                    Cap/wk
                  </th>
                  {data.week_labels.map((iso, i) => (
                    <th
                      key={iso}
                      className="text-center px-1 py-2 border-b border-r font-medium text-slate-500 whitespace-nowrap"
                      style={{ minWidth: 52 }}
                      data-testid={`team-availability-week-header-${i}`}
                    >
                      <div className="text-[10px] text-slate-400">W{i + 1}</div>
                      <div>{fmtWeekLabel(iso)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ role, resources }) => (
                  <React.Fragment key={role}>
                    <tr className="bg-slate-100/70">
                      <td
                        className="sticky left-0 bg-slate-100/70 z-10 px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-600 border-b border-r"
                        colSpan={2 + data.week_labels.length}
                        data-testid={`team-availability-role-row-${role}`}
                      >
                        {roleLabels[role] || role} · {resources.length}
                      </td>
                    </tr>
                    {resources.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`team-availability-row-${r.id}`}>
                        <td className="sticky left-0 bg-white hover:bg-slate-50/60 z-10 px-3 py-2 border-b border-r">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                              {(r.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('')}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-slate-800 truncate">{r.name}</div>
                              {r.email && (
                                <div className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
                                  <Mail className="w-2.5 h-2.5" />
                                  <span className="truncate">{r.email}</span>
                                </div>
                              )}
                            </div>
                            {r.overbooked_count > 0 && (
                              <Badge variant="outline" className="ml-auto text-rose-700 border-rose-300 bg-rose-50 text-[10px] px-1.5 py-0">
                                {r.overbooked_count}×
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="text-right px-2 py-2 border-b border-r text-slate-700 font-medium whitespace-nowrap">
                          {r.weekly_capacity_hours}h
                        </td>
                        {r.weekly_hours.map((h, i) => {
                          const blocked = r.blocked_week_indices.includes(i);
                          const cap = r.weekly_capacity_hours || 1;
                          const style = cellStyleFor({ blocked, hours: h, capacity: cap });
                          const ratio = blocked ? 0 : (h / cap);
                          const isHover =
                            hoverCell &&
                            hoverCell.resourceId === r.id &&
                            hoverCell.weekIdx === i;
                          return (
                            <td
                              key={i}
                              className={`relative text-center border-b border-r cursor-pointer hover:ring-2 hover:ring-slate-400/40 transition ${style.bg} ${style.border} ${style.pattern || ''}`}
                              style={{ minWidth: 52, height: 36 }}
                              onClick={() => {
                                if (h <= 0 && !blocked) return;
                                setReassignTarget({ resourceId: r.id, weekIso: data.week_labels[i] });
                              }}
                              onMouseEnter={() => setHoverCell({
                                resourceId: r.id,
                                resourceName: r.name,
                                weekIdx: i,
                                weekIso: data.week_labels[i],
                                hours: h,
                                capacity: cap,
                                blocked,
                                ratio,
                                label: style.label,
                              })}
                              onMouseLeave={() => setHoverCell(null)}
                              data-testid={`team-availability-cell-${r.id}-${i}`}
                              data-blocked={blocked ? '1' : '0'}
                              data-overbooked={!blocked && h > cap ? '1' : '0'}
                            >
                              <span className={`text-[10px] font-medium ${style.text}`}>
                                {blocked ? 'PTO' : (h > 0 ? Math.round(h) : '')}
                              </span>
                              {!blocked && h > cap && (
                                <Sparkles
                                  className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-rose-700/70 animate-pulse"
                                  aria-hidden="true"
                                />
                              )}
                              {isHover && (
                                <div className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1 bg-slate-900 text-white text-[11px] rounded shadow-lg px-2.5 py-1.5 whitespace-nowrap pointer-events-none">
                                  <div className="font-semibold">{r.name} · W{i + 1}</div>
                                  <div className="text-slate-300">{data.week_labels[i]}</div>
                                  {blocked ? (
                                    <div className="text-amber-300 mt-0.5">Blocked (PTO)</div>
                                  ) : (
                                    <>
                                      <div className="mt-0.5">
                                        <span className="text-slate-300">Allocated:</span> {h.toFixed(1)}h
                                      </div>
                                      <div>
                                        <span className="text-slate-300">Capacity:</span> {cap}h
                                      </div>
                                      <div>
                                        <span className="text-slate-300">Load:</span> {(ratio * 100).toFixed(0)}% · {style.label}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <SuggestReassignmentDialog
        open={!!reassignTarget}
        resourceId={reassignTarget?.resourceId}
        weekIso={reassignTarget?.weekIso}
        onClose={() => setReassignTarget(null)}
        onSaved={() => { setReassignTarget(null); load({ weeks }); }}
      />
    </div>
  );
}

function SummaryChip({ icon, label, value, tone = 'slate', testid }) {
  const toneCls =
    tone === 'rose' ? 'border-rose-200 bg-rose-50' :
    'border-slate-200 bg-white';
  return (
    <div className={`flex items-center gap-3 rounded-lg border ${toneCls} px-3 py-2`} data-testid={testid}>
      {icon}
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-lg font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-4 h-4 rounded border ${color}`} />
      <span>{label}</span>
    </span>
  );
}
